import Plan from '@/mstore/types/plan';
import { billingService } from '@/services';
import { toast } from '@/ui/overlays/toast';
import { makeAutoObservable } from 'mobx';

/** The plans API answers a refusal with 200 and `{ errors: [...] }`. */
const throwOnErrors = (data: any) => {
  if (data?.errors?.length) throw new Error(String(data.errors[0]));
  return data;
};

export interface Invoice {
  invoiceId: string;
  createdAt: number;
  amount: number;
  currency: string;
  /** Stripe's invoice status, plus `failed` */
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | 'failed';
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
}

const dataToInvoice = (data: any) => ({
  invoiceId: data.id,
  createdAt: data.created,
  amount: data.total,
  currency: data.currency,
  status: data.status,
  hostedInvoiceUrl: data.hostedInvoiceUrl,
  invoicePdf: data.invoicePdf,
});

export default class BillingStore {
  instance: Plan = new Plan();
  loading = false;
  loadingInvoices = false;
  cancelling = false;
  updating = false;
  planFetched = false;
  invoices: Invoice[] = [];
  invoicesHasMore = false;
  invoicesLimit = 100;
  invoicesPage = 1;

  constructor() {
    makeAutoObservable(this);
  }

  fetchPlan = async () => {
    this.loading = true;
    try {
      const data = await billingService.fetchPlan();
      this.instance = new Plan(data.data);
      this.planFetched = true;

      return this.instance;
    } catch (e) {
      throw e;
    } finally {
      this.loading = false;
    }
  };

  /** a new period starts over at page 1; `append` adds the next page to the list */
  fetchInvoices = async (
    period: {
      startTimestamp: number;
      endTimestamp: number;
    },
    append = false,
  ) => {
    this.loadingInvoices = true;
    if (!append) this.invoicesPage = 1;
    try {
      const data = await billingService.fetchInvoices({
        ...period,
        limit: this.invoicesLimit,
        page: this.invoicesPage,
      });
      const list: Invoice[] = data.list.map((d: any) => dataToInvoice(d));
      this.invoices = append ? [...this.invoices, ...list] : list;
      this.invoicesHasMore = data.has_more;
    } catch (e) {
      throw e;
    } finally {
      this.loadingInvoices = false;
    }
  };

  loadMoreInvoices = async (period: {
    startTimestamp: number;
    endTimestamp: number;
  }) => {
    this.invoicesPage += 1;
    return this.fetchInvoices(period, true);
  };

  updatePlan = async (instance: Plan) => {
    this.updating = true;
    try {
      const data = instance.billing.last4
        ? await billingService.updatePlan(instance.toSaveData())
        : await billingService.upgradePlan(instance.toSaveData());
      if (data.errors) {
        throw 'Unexpected Error, please check your card details and try again';
      } else {
        this.instance = new Plan(data.data);
      }
    } catch (e) {
      throw e;
    } finally {
      this.updating = false;
    }
  };

  cancelPlan = async () => {
    this.cancelling = true;
    try {
      const data = throwOnErrors(await billingService.cancelPlan());
      this.instance = new Plan(data.data);
      toast.success('Plan cancelled');
    } catch (e) {
      throw e;
    } finally {
      this.cancelling = false;
    }
  };

  enablePlan = async () => {
    this.loading = true;
    try {
      throwOnErrors(await billingService.enablePlan());
      await this.fetchPlan();
    } catch (e) {
      throw e;
    } finally {
      this.loading = false;
    }
  };

  getOnboard = async () => {
    this.loading = true;
    try {
      return await billingService.getOnboard();
    } catch (e) {
      throw e;
    } finally {
      this.loading = false;
    }
  };

  changeCaptureLimit = async (limit: number) => {
    try {
      return throwOnErrors(await billingService.changeCaptureLimit(limit));
    } catch (e) {
      throw e;
    }
  };
}
